import { Box, Typography } from "@mui/material";
import { DataGrid, GridSortModel } from "@mui/x-data-grid";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { StratifyDocument } from "@stratify/shared";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectDocumentsError,
  selectDocumentsLoading,
} from "../store/documentsSelectors";
import { updateFilters } from "../store/documentsSlice";
import { useDocumentListColumns } from "./useDocumentListColumns";
import { DocumentListField } from "./types";
import { selectFilteredDocuments } from "../store/selectFilteredDocuments";

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      textAlign: "center",
    }}
  >
    <CloudUploadOutlinedIcon sx={{ fontSize: 48, mb: 1 }} color="action" />
    <Typography variant="body2" color="text.secondary">
      Drop documents here or use the upload button
    </Typography>
  </Box>
);

export const DocumentList: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const documents = useSelector(selectFilteredDocuments);
  const loading = useSelector(selectDocumentsLoading);
  const error = useSelector(selectDocumentsError);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        dispatch(
          updateFilters({
            sortBy: field as DocumentListField,
            sortOrder: sort === "asc" ? "asc" : "desc",
          })
        );
      } else {
        dispatch(
          updateFilters({
            sortBy: "createdAt",
            sortOrder: "desc",
          })
        );
      }
    },
    [dispatch]
  );

  const columns = useDocumentListColumns();

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <DataGrid<StratifyDocument>
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        loading={loading}
        rows={documents}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        disableRowSelectionOnClick
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        onRowClick={(params) => navigate(`/documents/${params.row.id}`)}
        sx={{ cursor: "pointer" }}
      />
    </Box>
  );
};
